import { Container, Graphics, Point, Sprite } from 'pixi.js';
import { getAsset } from './preloadGameAssets';
import { areBoxesCollide } from './helpers/collision';
import { GameEvent } from './events/gameEvent';
import { EventName } from './events/eventsName';
import { COLUMN_Z_INDEX } from 'constants/layout';
import { Card } from 'components/Card';
import { CardPlace, GamePlace, KING_VALUE } from 'constants/cards';
import { SolitaireGame } from './SolitaireGame';
import { Layout } from './LayoutManager';
import gsap, { Power1 } from 'gsap';
import { CARD_MOVE_TIME } from 'constants/move';
import { Store } from './store/gameStore';
import { GameName } from 'constants/difficulty';
import { ColumnBlockAnimation } from './ColumnBlockAnimation';
import { GameBoardPlace } from './GameBoardPlace';

export type Move = {
    list: string[];
    from: CardPlace;
    cardToActivate?: Card;
    cardToHide?: Card;
    target: GamePlace;
};

export class Column extends GameBoardPlace {
    deckButton: Graphics;
    placeholder: Container;
    position = new Point();
    started = false;
    placeholderSprite?: Sprite;
    columnWrongMoveAnimation?: ColumnBlockAnimation;
    highlightActive = false;

    constructor(game: SolitaireGame, index: number) {
        super(game, index, GamePlace.TABLEAU);
        this.refreshHighlight = this.refreshHighlight.bind(this);
        this.onCardMove = this.onCardMove.bind(this);
        this.placeholder = new Container();
        this.container.addChild(this.placeholder);
        this.deckButton = new Graphics();
        this.position = Layout.getColumnPosition(index);
        this.container.position = this.position;
        this.drawPlaceholder();
        GameEvent.add(EventName.BACKGROUND_CLICKED, this.refreshHighlight);
        GameEvent.add(EventName.DRAG_DISABLED_CARD, this.onCardMove);
        GameEvent.add(EventName.DRAG_CARD, this.onCardMove);
        GameEvent.add(EventName.TOGGLE_CARD_HIGHLIGHTING, this.refreshHighlight);
    }

    async drawPlaceholder() {
        const txt = await getAsset('placeholders/slot-empty');
        this.placeholderSprite = Sprite.from(txt);
        this.placeholderSprite.anchor.set(0.5);
        this.placeholder.addChild(this.placeholderSprite);
        this.placeholder.position = new Point(0, 0);
        this.updatePlaceholder();
    }
    updatePlaceholder() {
        if (!this.placeholderSprite) return;
        const { width, height } = Layout.getCardSize();
        this.placeholderSprite.width = width;
        this.placeholderSprite.height = height;
    }

    getEvenColumnTotalHeight() {
        let height = 0;
        const { hidden, normal } = Layout.getCardGap();
        const cardHeigh = Layout.getCardSize();
        this.cards.forEach((card, index) => {
            if (index + 1 === this.cards.length) height += cardHeigh.height;
            else height += card.visible ? normal : hidden;
        });
        return height;
    }
    getShrinkColumGaps() {
        if (this.length === 0) return { visible: 1, hidden: 1 };
        const maxHeight = Layout.getMaxColumnHeight();
        const { hidden, normal } = Layout.getCardGap();
        const cardSize = Layout.getCardSize();
        let hiddenHeight = 0;
        const hiddenCards = [] as Card[];
        const visibleCards = [] as Card[];

        this.cards.forEach((card, index) => {
            if (card.visible) {
                visibleCards.push(card);
            } else {
                hiddenCards.push(card);
                hiddenHeight += hidden;
            }
        });
        let visibleGap = (maxHeight - hiddenHeight - cardSize.height) / (visibleCards.length - 1);
        if (visibleGap < hidden) {
            const equalGap = Math.max(0, (maxHeight - cardSize.height) / (this.length - 1));
            return { visible: equalGap, hidden: equalGap };
        }
        return { visible: visibleGap, hidden };
    }
    overDropArea(container: Container) {
        if (!this.lastCard) return false;
        return areBoxesCollide(this.lastCard.container, container);
    }
    validOrder(card: Card, lastCard: Card | null) {
        if (!lastCard) return true;
        if (!card.visible) return false;
        switch (Store.name) {
            case GameName.SPIDER:
                return card.cardColor === lastCard.cardColor && card.value === lastCard.value + 1;
            case GameName.KLONDIKE:
                return card.isRed !== lastCard.isRed && card.value === lastCard.value + 1;
            case GameName.FREECELL:
                return card.isRed !== lastCard.isRed && card.value === lastCard.value + 1;
        }
    }
    refresh() {
        this.reorderPlace(true);
        this.started = true;
        this.position = Layout.getColumnPosition(this.index);
        gsap.to(this.container.position, { ...this.position, ease: Power1.easeOut, duration: CARD_MOVE_TIME });
        this.updatePlaceholder();
        let cardActive = true;
        let lastCard = null as Card | null;
        let validOrder = 0;
        const onCompleteMove = (card: Card, triggerClick: boolean, index: number) => {
            if (triggerClick) {
                this.game.autoPlayActivated = true;
                card.triggerClick();
            }
            if (index === this.length - 1) {
                if (Store.isFreecell) this.refreshHighlight();
            }
        };
        for (let index = this.length - 1; index >= 0; index--) {
            let triggerClick = false;
            const card = this.cards[index];
            const pos = this.getCardPosition(index);

            if (cardActive) {
                if (this.validOrder(card, lastCard)) {
                    validOrder++;
                    if (validOrder === 13 && Store.isSpider) {
                        triggerClick = true;
                    }
                } else {
                    cardActive = false;
                }
            }
            card.place.position = pos;
            card.place.zIndex = COLUMN_Z_INDEX + index;
            if (card.shouldMove(pos)) {
                card.moveTo(pos, {}, () => {
                    onCompleteMove(card, triggerClick, index);
                });
            } else {
                card.reset();
                onCompleteMove(card, triggerClick, index);
            }

            if (this.cards.length === index + 1) {
                //last Card
                if (!card.visible) {
                    card.setVisible(true);
                    GameEvent.emit(EventName.REVEAL_CARD, card);
                }
            }
            card.setActive(cardActive);
            card.cardDisplay.castShadow(true);
            lastCard = card;
        }
    }
    onCardMove({ card }: { card: Card }) {
        const cardInColumn = this.cards.find((c) => c._id === card._id);
        if (!cardInColumn) {
            this.refreshHighlight();
        }
    }
    refreshHighlight() {
        if (Store.hasHighlighting()) {
            if (Store.cardHighlighting) this.showHighlight(this.lastCard, true);
            else this.removeHighlight();
        }
    }

    revealLastCard() {
        if (this.length === 0) return;
        const card = this.cards[this.length - 1];

        if (!card.visible) {
            card.setVisible(true);
            GameEvent.emit(EventName.REVEAL_CARD, card);
            card.setActive(true);
        }
    }

    getColumnLastCardPosition(): Point {
        return this.getCardPosition(this.length - 1);
    }
    getColumnNextCardPosition() {
        return this.getCardPosition(this.length);
    }
    getCardPosition(index: number) {
        const lastPosition = Layout.getColumnPosition(this.index);
        const { hidden, normal } = Layout.getCardGap();
        const totalHeight = this.getEvenColumnTotalHeight();
        let visibleGap = normal;
        let hiddenGap = hidden;
        if (totalHeight > Layout.getMaxColumnHeight()) {
            const sizes = this.getShrinkColumGaps();
            visibleGap = sizes.visible;
            hiddenGap = sizes.hidden;
        }

        for (let cardIndex = 0; cardIndex < index; cardIndex++) {
            lastPosition.y += !this.cards[cardIndex] || this.cards[cardIndex].visible ? visibleGap : hiddenGap;
        }
        return lastPosition;
    }
    resize(): void {
        if (!this.started) return;
        this.position = Layout.getColumnPosition(this.index);
        this.container.position = this.position;
        this.updatePlaceholder();
        for (let index = 0; index < this.length; index++) {
            const card = this.cards[index];
            const pos = this.getCardPosition(index);
            card.place.position = pos;
            card.reset();
        }
    }

    canDropCards(moveGroup: Card[]) {
        const lastCard = this.lastCard;
        if (lastCard) {
            return lastCard.canDropInColumn(moveGroup[0]);
        } else {
            if (Store.name === GameName.KLONDIKE) return moveGroup[0].value === KING_VALUE;
            if (Store.name === GameName.SPIDER) return true;
            if (Store.name === GameName.FREECELL) return true;
        }
        return false;
    }
    hasCardToMoveToFoundation(foundationCard: Card) {
        for (let index = this.length - 1; index >= 0; index--) {
            const card = this.cards[index];
            if (card.visible && foundationCard.canDropInFoundation(card)) {
                return card;
            }
        }
        return false;
    }
    buildGroupToRevealCardForFoundation(foundationCard: Card) {
        const moveGroup = [] as Card[];
        let toFoundationCard = null as Card | null;
        for (let index = this.length - 1; index >= 0; index--) {
            const card = this.cards[index];
            if (card.visible && foundationCard.canDropInFoundation(card)) {
                toFoundationCard = card;
                break;
            }
            moveGroup.push(card);
        }
        moveGroup.reverse();
        return { moveGroup, toFoundationCard };
    }

    showBlockAnimation() {
        if (this.columnWrongMoveAnimation) {
            this.columnWrongMoveAnimation.fadeOut();
        }
        this.columnWrongMoveAnimation = new ColumnBlockAnimation(this.container);
    }
    removeHighlight() {
        this.cards.forEach((card) => {
            card.activeAnimation();
        });
    }
    showHighlight(touchedCard?: Card | null, refreshHighlight = false) {
        if (!touchedCard) return;
        if (!touchedCard.visible) return;
        if (Store.isSpider) this.showHighlightSpider(touchedCard, refreshHighlight);
        if (Store.isFreecell) this.showHighlightFreeCell();
    }
    showHighlightFreeCell() {
        const limit = this.game.availableFreeCells + 1;

        const validOrderGroups = [] as string[];
        let lastCard = null as Card | null;

        for (let index = this.length - 1; index >= 0; index--) {
            const card = this.cards[index];
            if (this.validOrder(card, lastCard)) {
                validOrderGroups.push(card.code);
            } else {
                break;
            }
            lastCard = card;

            if (validOrderGroups.length === limit) break;
        }
        this.cards.forEach((card) => {
            if (validOrderGroups.includes(card.code)) card.activeAnimation();
            else card.inActiveAnimation();
        });
    }
    getMovingGroups() {
        if (!this.lastCard) return [];

        const moveGroups = [[this.lastCard]] as Card[][];
        let lastCard = this.lastCard;
        let currentIndex = 0;

        for (let index = this.length - 2; index >= 0; index--) {
            const card = this.cards[index];
            if (this.validOrder(card, lastCard)) {
                moveGroups[currentIndex].push(card);
            } else {
                currentIndex++;
                moveGroups.push([card]);
            }
            lastCard = card;
        }
        moveGroups.forEach((group) => {
            group.reverse();
        });
        return moveGroups;
    }
    getLastMoveGroup() {
        return this.getMovingGroups()[0] || [];
    }
    getNextMovingGroup() {
        const groups = this.getMovingGroups();
        return groups.length > 1 ? groups[1] : [];
    }
    showHighlightSpider(touchedCard: Card, refresh = false) {
        const removeHighlight = refresh && !Store.cardHighlighting;
        const validOrderGroups = [[]] as Card[][];
        let currentGroupIndex = 0;
        let highlightGroupIndex = 0;
        let lastCard = null as Card | null;
        if (removeHighlight) {
            this.highlightActive = false;
            highlightGroupIndex = -1;
            this.cards.forEach((card) => {
                card.activeAnimation();
            });
        } else {
            for (let index = this.length - 1; index >= 0; index--) {
                const card = this.cards[index];

                if (this.validOrder(card, lastCard)) {
                    validOrderGroups[currentGroupIndex].push(card);
                } else {
                    currentGroupIndex++;
                    validOrderGroups[currentGroupIndex] = [card] as Card[];
                }
                if (touchedCard._id === card._id) {
                    highlightGroupIndex = currentGroupIndex;
                }
                lastCard = card;
            }
            this.highlightActive = highlightGroupIndex !== 0;
        }
        validOrderGroups.forEach((group, index) => {
            if (index === highlightGroupIndex) {
                group.forEach((card) => {
                    card.activeAnimation();
                });
            } else {
                group.forEach((card) => {
                    card.inActiveAnimation();
                });
            }
        });
    }
    getNextCardPosition() {
        return this.getColumnNextCardPosition();
    }
    getLastCorrectOrder() {
        if (!this.lastCard) return [];

        const moveGroups = [this.lastCard] as Card[];
        let lastCard = this.lastCard;
        let currentIndex = 0;

        for (let index = this.length - 2; index >= 0; index--) {
            const card = this.cards[index];
            if (card.canDropInColumn(lastCard)) {
                moveGroups.push(card);
            } else {
                return moveGroups.reverse();
            }
            lastCard = card;
        }

        return moveGroups.reverse();
    }
    getLastValidOrder() {
        //for spider - cards create order - but to in one color.
        if (!this.lastCard) return [];

        const moveGroups = [this.lastCard] as Card[];
        let lastCard = this.lastCard;

        for (let index = this.length - 2; index >= 0; index--) {
            const card = this.cards[index];

            if (this.validOrder(card, lastCard)) {
                moveGroups.push(card);
            } else {
                return moveGroups.reverse();
            }
            lastCard = card;
        }

        return moveGroups.reverse();
    }

    getSequences(): Card[][] {
        const sequences = [] as Card[][];
        let lastCard = null as Card | null;
        let currentSequence = [] as Card[];
        for (let index = this.length - 1; index >= 0; index--) {
            const card = this.cards[index];
            if (this.validOrder(card, lastCard)) {
                currentSequence.unshift(card);
            } else {
                sequences.unshift(currentSequence);
                currentSequence = [card];
            }
            lastCard = card;
        }
        if (currentSequence.length) {
            sequences.unshift(currentSequence);
        }
        return sequences;
    }

    addCardAfter(lastCard: Card, newCard: Card): number {
        const index = this.cards.indexOf(lastCard);
        this.cards.splice(index + 1, 0, newCard);

        return index + 1;
    }
    addCardBefore(firstCard: Card, newCard: Card): number {
        const index = this.cards.indexOf(firstCard);
        this.cards.splice(index, 0, newCard);
        return index;
    }
    reorderPlace(noReset = false) {
        this.cards.forEach((card, index) => {
            card.place.order = index;
            card.place.zIndex = COLUMN_Z_INDEX + index;
            if (!noReset) card.reset();
        });
    }
}
